import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { MatInput } from '@angular/material/input';
import { ApplicantAttachment } from '@app/models/recruiting/applicant-attachments.model';
import { Applicant } from '@app/models/recruiting/applicant.model';
import { JobAttachment } from '@app/models/recruiting/job-attachments.model';
import { NotifyService } from '@app/services';
import { JobAttachmentTypes } from '../../enums/job-attachment-types.enum';
import { ApplicantProfileService } from '../../services';

type linkAttachmentState = 'Add' | 'Edit' | 'Delete';

@Component({
    selector: 'app-links-card',
    templateUrl: './links-card.component.html',
    styleUrls: ['./links-card.component.scss'],
})
export class LinksCardComponent implements OnInit {
    @Input() applicant: Applicant;
    @ViewChild('link') linkInput: ElementRef;
    applicantAttachments: ApplicantAttachment[] = [];
    linkAttachments: ApplicantAttachment[] = [];
    jobAttachments: JobAttachment[] = [];
    jobAttachmentTypes = JobAttachmentTypes;
    selectedLinkAttachment: ApplicantAttachment;
    additionalLinkAttachmentName = '';
    additionalLinkAttachmentContent = '';
    selectedLinkField: MatInput;
    selectedIndex: number;
    showSaveAndCancel = false;
    isDeleteLinkDialogOpen = false;
    isAddAdditionalLinkDialogOpen = false;
    isLoading = false;
    cancelButtonStates: boolean[];
    editStates: linkAttachmentState[];

    constructor(
        public applicantProfileService: ApplicantProfileService,
        private notify: NotifyService
    ) {}

    async ngOnInit(): Promise<void> {
        try {
            await this.populateLinks();
        } catch (err) {
            this.notify.error(err);
        }
    }

    async populateLinks(): Promise<void> {
        this.jobAttachments = await this.applicantProfileService.getJobAttachments(this.applicant.jobPostingId);
        this.applicantAttachments = await this.applicantProfileService.getApplicantAttachments(this.applicant.id);

        this.processJobAttachments();
        this.linkAttachments = this.filterApplicantAttachmentsByType(this.jobAttachmentTypes.Text, 'content');
        this.cancelButtonStates = new Array(this.linkAttachments.length).fill(false);
        this.createEditStatesForLinkFields();
    }

    async addAdditionalLinkAttachment(): Promise<void> {
        this.closeAddAdditionalLinkDialog();

        this.isLoading = true;

        try {
            const newAdditionalLinkAttachment = new ApplicantAttachment({
                applicantId: this.applicant.id,
                jobAttachmentId: null,
                fileId: null,
                content: this.additionalLinkAttachmentContent,
                name: this.additionalLinkAttachmentName,
            });

            await newAdditionalLinkAttachment.save();
            this.notify.success({
                key: 'applicant-tracker.addedLinkNameLink',
                params: { linkName: newAdditionalLinkAttachment.name },
            });

            // Reset form values
            this.additionalLinkAttachmentName = '';
            this.additionalLinkAttachmentContent = '';

            this.applicantProfileService.clearApplicantAttachments();

            // Repopulate links
            await this.populateLinks();
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    createEditStatesForLinkFields(): void {
        this.editStates = this.linkAttachments.map((linkAttachment: ApplicantAttachment) => {
            return linkAttachment.content ? 'Edit' : 'Add';
        });
    }

    // Process and add any job attachments that are not in the applicant attachment array.
    processJobAttachments(): void {
        // Filter the jobAttachments based on if a corresponding applicant attachment is found.
        // This means theres a value that exists for the applicant and we should use the applicant attachment.
        const filteredJobAttachments = this.filterByJobAttachments();

        if (filteredJobAttachments.length) {
            this.addToApplicantAttachments(filteredJobAttachments);
        }
    }

    filterByJobAttachments(): JobAttachment[] {
        return this.jobAttachments.filter((jobAttachment: JobAttachment) => {
            return !this.applicantAttachments.find((applicantAttachment: ApplicantAttachment) => {
                return applicantAttachment.jobAttachmentId === jobAttachment.id;
            });
        });
    }

    addToApplicantAttachments(jobAttachments: JobAttachment[]): void {
        this.applicantAttachments = [
            ...this.applicantAttachments,
            ...this.convertJobAttachmentsToApplicantAttachments(jobAttachments),
        ];
    }

    // We need to convert JobAttachments as they are only a indication of what files a user can upload but to actually save files we must save them as Applicant Attachments
    convertJobAttachmentsToApplicantAttachments(jobAttachments: JobAttachment[]): ApplicantAttachment[] {
        return jobAttachments.map((jobAttachment: JobAttachment) => {
            const applicantAttachment = this.createApplicantAttachmentFromJobAttachment(jobAttachment);
            return applicantAttachment;
        });
    }

    createApplicantAttachmentFromJobAttachment(jobAttachment: JobAttachment): ApplicantAttachment {
        const newApplicantAttachment = new ApplicantAttachment({
            applicantId: this.applicant.id,
            jobAttachmentId: jobAttachment?.id,
            fileId: jobAttachment.fileId ? jobAttachment.fileId : undefined,
            content: jobAttachment.link ? jobAttachment.link : undefined,
        });
        newApplicantAttachment.jobAttachment = jobAttachment;
        return newApplicantAttachment;
    }

    filterApplicantAttachmentsByType(type: JobAttachmentTypes, propName: string): ApplicantAttachment[] {
        return this.applicantAttachments.filter((applicantAttachment: ApplicantAttachment) => {
            return applicantAttachment.jobAttachment?.jobAttachmentTypeId === type || !!applicantAttachment[propName];
        });
    }

    async addLink(applicantAttachment: ApplicantAttachment, index: number): Promise<void> {
        if (!this.linkInput.nativeElement.value) {
            return;
        }

        applicantAttachment.content = this.linkInput.nativeElement.value;
        applicantAttachment.fileId = undefined;
        this.editStates[index] = 'Edit';
        this.showSaveAndCancel = false;

        await applicantAttachment.save();
        this.notify.success({
            key: 'applicant-tracker.addedLinkNameLink',
            params: { linkName: applicantAttachment.jobAttachment?.name || applicantAttachment.name },
        });
    }

    editMode(applicantAttachment: ApplicantAttachment, index: number): void {
        this.editStates[index] = 'Delete';
        this.selectedIndex = index;
        this.showSaveAndCancel = true;
        this.selectedLinkAttachment = applicantAttachment;
    }

    async deleteLink(): Promise<void> {
        await this.selectedLinkAttachment.delete();
        this.notify.info({
            key: 'applicant-tracker.deletedLinkNameLink',
            params: { linkName: this.selectedLinkAttachment.jobAttachment?.name || this.selectedLinkAttachment.name },
        });
        this.applicantAttachments = this.applicantAttachments.filter(
            (item: ApplicantAttachment) => this.selectedLinkAttachment.id !== item.id
        );
        this.linkAttachments.splice(this.selectedIndex, 1);
        this.closeDeleteLinkDialog();
        this.showSaveAndCancel = false;
        this.applicantProfileService.clearApplicantAttachments();
        this.applicantProfileService.clearJobAttachments();
        // Reload the data
        this.ngOnInit();
    }

    closeDeleteLinkDialog(): void {
        this.isDeleteLinkDialogOpen = false;
    }

    openDeleteLinkDialog(applicantAttachment: ApplicantAttachment, index: number): void {
        this.isDeleteLinkDialogOpen = true;

        this.selectedLinkAttachment = applicantAttachment;
        this.selectedIndex = index;
    }

    closeAddAdditionalLinkDialog(): void {
        this.isAddAdditionalLinkDialogOpen = false;
    }

    openAddAdditionalLinkDialog(): void {
        this.isAddAdditionalLinkDialogOpen = true;

        // Reset form values
        this.additionalLinkAttachmentName = '';
        this.additionalLinkAttachmentContent = '';
    }

    onFocusUpdateCancelButton(index: number): void {
        this.cancelButtonStates[index] = true;
    }

    onBlurUpdateCancelButton(linkEl: HTMLInputElement, index: number): void {
        if (linkEl.value) {
            return;
        }
        this.cancelButtonStates[index] = false;
    }

    clearAndHideCancel(applicantAttachment: ApplicantAttachment, index: number): void {
        this.cancelButtonStates[index] = false;
        applicantAttachment.content = '';
    }

    cancelEditMode(index: number): void {
        this.editStates[index] = 'Edit';
        this.showSaveAndCancel = false;
    }
}
